import PropTypes from "prop-types";
import React, { Component } from "react";
import { DragDropContext } from "react-dnd";
import HTML5Backend from "react-dnd-html5-backend";
import "styles/less/style_v2.less";

import MuiThemeProvider from "material-ui/styles/MuiThemeProvider";
import getMuiTheme from "material-ui/styles/getMuiTheme";
import LenndMuiTheme from "components/Global/Themes/LenndMuiTheme";
import PlatformLoading from "components/Platform/PlatformLoading";

require("es6-promise").polyfill();
require("url-search-params-polyfill");
require("classlist-polyfill");

const shouldDeferStateConfiguration = ({ routes, userId, orgId, eventId }) => {
  const isEventRoute = routes.find(r =>
    [
      "editEventProfile",
      "PortalV2_LoggedInView",
      "PortalV2_LoggedOutView",
      "eventLightGate"
    ].includes(r.name)
  );
  const isOrgRoute = routes.find(r =>
    ["organization", "OrgLightGate", "formsV3_Edit"].includes(r.name)
  );

  if (isEventRoute && isOrgRoute) {
    return !userId || !orgId || !eventId;
  } else if (isEventRoute) {
    return !userId || !eventId;
  } else if (isOrgRoute) {
    return !userId || !orgId;
  }
  return !userId;
};

@DragDropContext(HTML5Backend)
class View extends Component {
  // Use refs to track previous values without causing re-renders
  previousOrgId = null;
  previousEventId = null;

  componentDidMount() {
    const { user } = this.props;
    // If we already have a user, we can initialize immediately
    if (user.id) {
      this.initializeSession();
    } else {
      this.props.checkSession();
    }
  }

  componentDidUpdate(prevProps) {
    const { user, params, isSessionCheckComplete } = this.props;

    // Fetch flags when session check completes and we have a user
    if (
      !prevProps.isSessionCheckComplete &&
      isSessionCheckComplete &&
      user.id
    ) {
      this.initializeSession();
      return;
    }

    // Get current IDs
    const currentOrgId = params.orgId;
    const currentEventId = parseInt(params.eventId, 10) || null;

    // Check if org or event ID has changed
    const hasOrgChanged = currentOrgId !== this.previousOrgId;
    const hasEventChanged = currentEventId !== this.previousEventId;

    if (hasOrgChanged || hasEventChanged) {
      this.configureGlobalState();
      this.previousOrgId = currentOrgId;
      this.previousEventId = currentEventId;
    }
  }

  initializeSession = () => {
    this.configureGlobalState();
  };

  configureGlobalState() {
    const { user, params } = this.props;
    const userId = parseInt(user.id, 10) || null;
    const currentOrgId = params.orgId;
    const currentEventId = parseInt(params.eventId, 10) || null;

    const stateConfig = {
      user: {
        key: user.email,
        firstName: user.fname,
        lastName: user.lname,
        email: user.email,
        avatar: user.photoUrl,
        custom: {
          userId,
          currentOrgId,
          currentEventId
        }
      }
    };

    // Configure global state with the state config
    this.props.fetchFlags(stateConfig);
  }

  render() {
    const { user, routes, isSessionCheckComplete, params } = this.props;

    const userId = parseInt(user.id, 10) || null;
    const currentOrgId = params.orgId;
    const currentEventId = parseInt(params.eventId, 10) || null;

    const shouldDefer = shouldDeferStateConfiguration({
      routes,
      userId,
      orgId: currentOrgId,
      eventId: currentEventId
    });

    const showLoading = shouldDefer && !isSessionCheckComplete;

    return (
      <MuiThemeProvider muiTheme={getMuiTheme(LenndMuiTheme)}>
        {showLoading ? <PlatformLoading /> : this.props.children}
      </MuiThemeProvider>
    );
  }
}

View.propTypes = {
  children: PropTypes.element,
  checkSession: PropTypes.func.isRequired,
  fetchFlags: PropTypes.func.isRequired,
  isSessionCheckComplete: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  orgDetails: PropTypes.object.isRequired,
  eventDetails: PropTypes.object.isRequired,
  routes: PropTypes.array.isRequired,
  params: PropTypes.object.isRequired
};

export default View;
